import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    linkedSignal,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { FormField, form, required, submit, validate } from "@angular/forms/signals";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25LoadingInlineStaticComponent } from "../../modules/s25-loading/s25.loading.inline.static.component";
import { PasswordResetService } from "../../services/password.reset.service";
import { passwordValidator } from "../../services/password.validation";
import { StateService } from "../../services/state.service";
import { S25States } from "../../services/state/state.map";
import { S25Util } from "../../util/s25-util";

const RESET_COMPLETE_ERROR = "There was a problem resetting your password. Please contact your system administrator.";

@TypeManagerDecorator("s25-ng-password-reset")
@Component({
    selector: "s25-ng-password-reset",
    host: {
        class: "password-reset-host",
        "[class.password-reset-host--connect25]": "connect25()",
    },
    template: `
        @if (connect25()) {
            <div class="cn-login cn-login--reset" data-screen-label="Connect25 Password Reset">
                <main class="cn-login__form cn-login__form--desktop">
                    <div class="cn-login__form-bg" aria-hidden="true"></div>
                    <div class="cn-login__form-inner">
                        <ng-container [ngTemplateOutlet]="formContent" />
                    </div>
                </main>
            </div>
        } @else {
            <div class="password-reset-page">
                <div class="password-reset-card">
                    <ng-container [ngTemplateOutlet]="formContent" />
                </div>
            </div>
        }

        <ng-template #formContent>
            <header class="password-reset-header">
                <h1>Reset Your Password</h1>
                @if (!completed()) {
                    <p>Choose a new password for your account.</p>
                }
            </header>

            @if (missingToken()) {
                <p class="password-reset-error">
                    This password reset link is invalid or has expired. Request a new reset link from the sign-in page.
                </p>
            } @else if (completed()) {
                <p class="password-reset-success">Your password was successfully reset.</p>
                <p class="password-reset-hint">Sign in with your new password.</p>
            } @else {
                <form class="password-reset-form" method="post" action="" (ngSubmit)="resetPassword($event)">
                    @let passwordField = resetForm.password();
                    <div class="form-row">
                        <label for="password-reset-new">New Password</label>
                        <input
                            id="password-reset-new"
                            type="password"
                            autocomplete="new-password"
                            class="c-input"
                            [formField]="resetForm.password"
                        />
                    </div>
                    @if (passwordField.touched() && passwordField.invalid()) {
                        <p class="password-reset-error">{{ passwordField.errors()?.[0]?.message }}</p>
                    }

                    @let confirmField = resetForm.confirmPassword();
                    <div class="form-row">
                        <label for="password-reset-confirm">Confirm new password</label>
                        <input
                            id="password-reset-confirm"
                            type="password"
                            autocomplete="new-password"
                            class="c-input"
                            [formField]="resetForm.confirmPassword"
                        />
                    </div>
                    @if (confirmField.touched() && confirmField.invalid()) {
                        <p class="password-reset-error">{{ confirmField.errors()?.[0]?.message }}</p>
                    }

                    <p class="password-reset-hint">{{ passwordHintMessage }}</p>

                    @if (submitError()) {
                        <p class="password-reset-error">{{ submitError() }}</p>
                    }

                    <div class="password-reset-actions">
                        <button
                            type="submit"
                            class="aw-button aw-button--primary"
                            [disabled]="resetForm().invalid() || resetForm().submitting()"
                        >
                            @if (resetForm().submitting()) {
                                <s25-ng-loading-inline-static text="Resetting…"></s25-ng-loading-inline-static>
                            } @else {
                                Reset Password
                            }
                        </button>
                    </div>
                </form>
            }

            <p class="password-reset-return">
                <button type="button" class="password-reset-return-link" (click)="returnToApp()">
                    {{ returnLabel() }}
                </button>
            </p>
        </ng-template>
    `,
    styles: `
        :host.password-reset-host--connect25 {
            display: block;
            min-height: 100vh;
            font-family:
                "Inter",
                system-ui,
                -apple-system,
                sans-serif;
            color: var(--cn-fg-1);
        }

        .cn-login--reset {
            display: flex;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            background: var(--cn-bg-alt);
            padding: 2rem;
        }

        .cn-login--reset .cn-login__form {
            position: relative;
            width: 100%;
            max-width: 480px;
        }

        .password-reset-page {
            display: flex;
            justify-content: center;
            padding: 3rem 1rem;
        }

        .password-reset-card,
        .cn-login--reset .cn-login__form-inner {
            width: 100%;
            max-width: 420px;
            background: var(--color--surface-1-bg, #fff);
            border: 1px solid var(--color--border, #ddd);
            border-radius: 8px;
            padding: 2rem;
            box-shadow: var(--shadow--dropdown, 0 4px 12px rgba(0, 0, 0, 0.08));
        }

        .password-reset-header h1 {
            margin: 0 0 0.5rem;
            font-size: 1.5rem;
            text-align: center;
        }

        .password-reset-header p {
            margin: 0 0 1.5rem;
            color: var(--color--text-muted, #737373);
            text-align: center;
        }

        .password-reset-form {
            display: grid;
            row-gap: 0.75rem;
        }

        .form-row {
            display: flex;
            column-gap: 1rem;
            row-gap: 0.25rem;
            justify-content: center;
            align-items: center;
        }

        .form-row label {
            width: 9ch;
            font-weight: 600;
            text-align: right;
        }

        .form-row .c-input {
            width: 22ch;
        }

        .password-reset-actions {
            display: flex;
            justify-content: center;
            padding-top: 0.5rem;
        }

        .password-reset-error {
            color: var(--error-message-color, #c00);
            margin: 0;
            text-align: center;
        }

        .password-reset-success {
            color: var(--color--success-text, #2e7d32);
            font-weight: 600;
            margin: 0 0 0.5rem;
            text-align: center;
        }

        .password-reset-hint {
            color: var(--color--text-muted, #737373);
            font-size: 0.875rem;
            margin: 0;
            text-align: center;
        }

        .password-reset-return {
            margin: 1.5rem 0 0;
            text-align: center;
        }

        .password-reset-return-link {
            padding: 0;
            border: none;
            background: none;
            color: var(--color--link-text);
            font-size: 0.875rem;
            text-decoration: none;
            cursor: pointer;
        }

        .password-reset-return-link:hover,
        .password-reset-return-link:focus {
            color: var(--color--link-text-hover);
            text-decoration: underline;
        }

        @media screen and (width <= 700px) {
            .form-row {
                flex-direction: column;
                align-items: stretch;
            }

            .form-row label {
                width: auto;
                text-align: left;
            }

            .form-row .c-input {
                width: 100%;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormField, FormsModule, NgTemplateOutlet, S25LoadingInlineStaticComponent],
})
export class PasswordResetComponent {
    private static readonly REDIRECT_DELAY_MS = 2000;
    readonly passwordHintMessage = passwordValidator.HintMessage;
    readonly connect25 = input(false);

    readonly returnLabel = computed(() => (this.connect25() ? "Return to Connect25" : "Return to 25Live"));

    readonly resetToken = linkedSignal(() => StateService.getSearch().pw_reset_token?.trim() ?? "");
    readonly missingToken = computed(() => !this.resetToken());

    readonly submitError = signal<string | undefined>(undefined);
    readonly completed = signal(false);

    readonly formModel = signal({ password: "", confirmPassword: "" });
    readonly resetForm = form(this.formModel, (schema) => {
        required(schema.password, { message: "Enter a new password" });
        validate(schema.password, (field) => {
            const password = field.value();
            if (!password) return;
            const validationError = passwordValidator.getError(password);
            if (validationError) {
                return {
                    kind: validationError,
                    message: passwordValidator.getMessage(validationError),
                };
            }
        });
        required(schema.confirmPassword, { message: "Confirm your new password" });
        validate(schema.confirmPassword, (field) => {
            const confirmPassword = field.value();
            const password = this.formModel().password;
            if (confirmPassword && password !== confirmPassword) {
                return { kind: "password-mismatch", message: "Passwords do not match." };
            }
        });
    });

    resetPassword(event?: Event) {
        event?.preventDefault();

        if (this.missingToken() || this.completed() || this.resetForm().invalid() || this.resetForm().submitting()) {
            return;
        }

        return submit(this.resetForm, async () => {
            this.submitError.set(undefined);
            const token = this.resetToken();
            const { password } = this.resetForm().value();

            const [, err] = await S25Util.Maybe(PasswordResetService.completeReset(token, password));
            if (err) {
                this.submitError.set(RESET_COMPLETE_ERROR);
                return;
            }

            this.formModel.set({ password: "", confirmPassword: "" });
            this.stripLeakedCredentialsFromBrowserUrl();
            this.completed.set(true);
            this.redirectAfterSuccess();
        });
    }

    returnToApp() {
        if (this.connect25()) {
            void StateService.go(S25States.names.seHome, null, {});
        } else {
            // home is an already-entered ancestor, so it must be reloaded for the initialRedirect
            // resolver to re-run and route to the user's preferred landing page
            void StateService.go(S25States.names.home, null, { reload: S25States.names.home });
        }
    }

    private stripLeakedCredentialsFromBrowserUrl(): void {
        if (!window.location.search) {
            return;
        }
        window.history.replaceState(null, "", window.location.pathname + window.location.hash);
    }

    private redirectAfterSuccess(): void {
        window.setTimeout(() => {
            if (this.connect25()) {
                void StateService.go(S25States.names.seLogin, null, { location: "replace" });
            } else {
                // home is an already-entered ancestor, so it must be reloaded for the initialRedirect
                // resolver to re-run and route to the user's preferred landing page
                void StateService.go(S25States.names.home, null, {
                    reload: S25States.names.home,
                    location: "replace",
                });
            }
        }, PasswordResetComponent.REDIRECT_DELAY_MS);
    }
}
